import React, { useEffect, useRef } from 'react';
import styles from './index.module.scss';
import { SearchIcon } from '../../../icons/CreationLanderIcons';
import { useTrackingContext } from '../../../hooks';
import InviteBanner from '../../InviteBanner';

const TAB_COLORS = [
  { varName: '--bfi-yellow', text: '#000' },
  { varName: '--bfi-orange', text: '#000' },
  { varName: '--bfi-deep-pink', text: '#fff' },
  { varName: '--bfi-fuchsia', text: '#000' },
  { varName: '--bfi-green-yellow', text: '#000' },
  { varName: '--bfi-dodger-blue', text: '#000' },
  { varName: '--bfi-blue-violet', text: '#fff' },
  { varName: '--bfi-cyan', text: '#000' },
  { varName: '--bfi-lime', text: '#000' },
];

const Tabs = ({
  zones,
  tabIndex,
  animateToTab,
  isWeb,
  showSearchView,
  onSearchViewToggle,
  searchQuery,
  onSearchQueryChange,
  dragOffset,
  enableTransition,
  canShowInviteBanner,
}) => {
  const searchInputRef = useRef(null);
  const { trackContentAction } = useTrackingContext();

  // Auto-scroll tab into view when tab changes
  useEffect(() => {
    const tabEl = document.querySelectorAll(`.${styles.tab}`)[tabIndex];
    if (tabEl) {
      const { left, right } = tabEl.getBoundingClientRect();
      const isOffScreen = left < 0 || right > window.innerWidth;
      if (isOffScreen) {
        tabEl.scrollIntoView({
          behavior: 'smooth',
          inline: 'center',
          block: 'nearest',
        });
      }
    }
  }, [tabIndex]);

  // Focus search input when SearchView opens
  useEffect(() => {
    if (showSearchView && searchInputRef.current) {
      // Small delay to ensure animations don't interfere
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
    }
  }, [showSearchView]);

  const onTabClick = (index) => {
    // Use carousel animation for tab changes
    if (animateToTab && index !== tabIndex) {
      const targetZone = zones[index];

      trackContentAction({
        unit_type: 'nav',
        unit_name: 'tabs',
        item_type: 'button',
        item_name: targetZone?.name,
        position_in_unit: index,
        action_type: 'select',
        action_value: targetZone?.name,
      });

      animateToTab(index);
    }
  };

  const onSearchIconClick = () => {
    trackContentAction({
      unit_type: 'nav',
      unit_name: 'search',
      item_type: 'button',
      item_name: 'search',
      position_in_unit: 0,
      action_type: 'open',
      action_value: 'search',
    });

    onSearchViewToggle();
  };

  const onSearchInputFocus = () => {
    trackContentAction({
      unit_type: 'nav',
      unit_name: 'search',
      item_type: 'input',
      item_name: 'search',
      position_in_unit: 1,
      action_type: 'select',
      action_value: 'input',
    });
  };

  return (
    <div
      className={`${styles.headerContainer} ${isWeb ? styles.web : ''} ${showSearchView ? styles.searchActive : ''}`}
    >
      {canShowInviteBanner && !showSearchView && (
        <InviteBanner variant="creationLander" />
      )}

      <div className={styles.slidingContainer}>
        {/* Tabs - slide left when search opens */}
        <div
          className={styles.tabsView}
          style={{
            transform: showSearchView
              ? `translateX(calc(-100vw + ${dragOffset || 0}px))`
              : 'translateX(0)',
            transition: enableTransition ? 'transform 300ms ease-out' : 'none',
          }}
        >
          <div className={styles.tabs}>
            {zones.map((zone, index) => (
              <button
                className={`${styles.tab} ${tabIndex === index ? styles.selected : ''} ${!isWeb ? styles.extraMargin : ''}`}
                onClick={() => onTabClick(index)}
                key={index}
                type="button"
                style={
                  tabIndex === index
                    ? {
                        '--tab-bg': `var(${TAB_COLORS[index % TAB_COLORS.length].varName})`,
                        '--tab-text':
                          TAB_COLORS[index % TAB_COLORS.length].text,
                      }
                    : {}
                }
              >
                {zone.display_name}
              </button>
            ))}
          </div>
        </div>

        {/* Search input - starts as peek (48px circle), expands to full width - only in app */}
        {!isWeb && (
          <div
            className={styles.searchView}
            style={{
              width: showSearchView ? '100vw' : 'auto',
              transform: showSearchView
                ? `translateX(${dragOffset || 0}px)`
                : 'translateX(0)',
              transition: enableTransition
                ? 'width 300ms ease-out, transform 300ms ease-out'
                : 'none',
            }}
          >
            <div
              className={`${styles.searchInputContainer} ${!showSearchView ? styles.peek : ''}`}
              onClick={!showSearchView ? onSearchIconClick : undefined}
              style={{
                width: showSearchView
                  ? dragOffset && dragOffset > 0
                    ? `calc(100vw - ${dragOffset}px)`
                    : '100vw'
                  : undefined,
                transition: enableTransition
                  ? 'width 300ms ease-out, padding 300ms ease-out, border-radius 300ms ease-out'
                  : 'none',
              }}
            >
              <div
                className={styles.searchIcon}
                style={{ '--icon-size': !showSearchView ? '88px' : '28px' }}
              >
                <SearchIcon />
                {!showSearchView && <span>Search</span>}
              </div>
              <input
                ref={searchInputRef}
                className={styles.searchInput}
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => onSearchQueryChange(e.target.value)}
                onFocus={onSearchInputFocus}
                autoFocus={showSearchView}
                style={{
                  width: showSearchView ? '100%' : '0',
                  opacity: showSearchView ? 1 : 0,
                  transition: 'width 300ms ease-out, opacity 300ms ease-out',
                }}
              />
              {showSearchView && searchQuery && (
                <button
                  className={styles.clearButton}
                  onClick={() => onSearchQueryChange('')}
                  aria-label="Clear search"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="17"
                    height="17"
                    viewBox="0 0 17 17"
                    fill="none"
                  >
                    <rect
                      x="11.8427"
                      y="11.8055"
                      width="1.38889"
                      height="1.38889"
                      fill="white"
                    />
                    <rect
                      x="6.28705"
                      y="6.25"
                      width="1.38889"
                      height="1.38889"
                      fill="white"
                    />
                    <rect
                      x="10.4537"
                      y="10.4166"
                      width="1.38889"
                      height="1.38889"
                      fill="white"
                    />
                    <rect
                      x="4.89819"
                      y="4.86108"
                      width="1.38889"
                      height="1.38889"
                      fill="white"
                    />
                    <rect
                      x="9.06482"
                      y="9.02783"
                      width="1.38889"
                      height="1.38889"
                      fill="white"
                    />
                    <rect
                      x="3.50928"
                      y="3.47217"
                      width="1.38889"
                      height="1.38889"
                      fill="white"
                    />
                    <rect
                      x="7.67596"
                      y="7.63892"
                      width="1.38889"
                      height="1.38889"
                      fill="white"
                    />
                    <rect
                      width="1.38889"
                      height="1.38889"
                      transform="matrix(1 -4.37114e-07 -4.37114e-07 -1 11.8427 4.86108)"
                      fill="white"
                    />
                    <rect
                      width="1.38889"
                      height="1.38889"
                      transform="matrix(1 -4.37114e-07 -4.37114e-07 -1 6.28705 10.4166)"
                      fill="white"
                    />
                    <rect
                      width="1.38889"
                      height="1.38889"
                      transform="matrix(1 -4.37114e-07 -4.37114e-07 -1 10.4537 6.25)"
                      fill="white"
                    />
                    <rect
                      width="1.38889"
                      height="1.38889"
                      transform="matrix(1 -4.37114e-07 -4.37114e-07 -1 4.89819 11.8055)"
                      fill="white"
                    />
                    <rect
                      width="1.38889"
                      height="1.38889"
                      transform="matrix(1 -4.37114e-07 -4.37114e-07 -1 9.06482 7.63892)"
                      fill="white"
                    />
                    <rect
                      width="1.38889"
                      height="1.38889"
                      transform="matrix(1 -4.37114e-07 -4.37114e-07 -1 3.50928 13.1945)"
                      fill="white"
                    />
                    <rect
                      width="1.38889"
                      height="1.38889"
                      transform="matrix(1 -4.37114e-07 -4.37114e-07 -1 7.67596 9.02783)"
                      fill="white"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Tabs;
